import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Trois cartes alignées sur la page Finances : solde, entrées, sorties (FBU).
 * Sur mobile : bandeau horizontal compact (scroll) type app native ; sur md+ : grille classique.
 * @param {{ soldeActuel: number, totalEntrees: number, totalSorties: number, loading?: boolean }} props
 */
export default function LedgerFinanceKpiCards({ soldeActuel, totalEntrees, totalSorties, loading = false }) {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const fmt = (n) => (loading ? '—' : Number(n || 0).toLocaleString(localeFmt));

  const items = [
    {
      key: 'solde',
      accent: 'border-l-[#2f6fd4]',
      label: t('financial.kpi.balance'),
      sub: t('financial.kpi.balanceSub'),
      value: fmt(soldeActuel),
    },
    {
      key: 'entrees',
      accent: 'border-l-emerald-500',
      label: t('financial.kpi.in'),
      sub: t('financial.kpi.inSub'),
      value: fmt(totalEntrees),
    },
    {
      key: 'sorties',
      accent: 'border-l-red-500',
      label: t('financial.kpi.out'),
      sub: t('financial.kpi.outSub'),
      value: fmt(totalSorties),
    },
  ];

  return (
    <>
      <div className="-mx-0.5 flex gap-2 overflow-x-auto px-0.5 pb-1 touch-pan-x [scrollbar-width:none] md:hidden [&::-webkit-scrollbar]:hidden snap-x snap-mandatory">
        {items.map((it) => (
          <div
            key={it.key}
            className={`max-w-[42vw] min-w-[9.25rem] shrink-0 snap-start rounded-2xl border border-slate-200/90 border-l-4 bg-white p-3 shadow-sm ${it.accent}`}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">{it.label}</p>
            <p className="mt-0.5 text-base font-bold tabular-nums tracking-tight text-[#0f3350]">
              {it.value} <span className="text-[10px] font-semibold text-slate-600">FBU</span>
            </p>
            <p className="mt-1 line-clamp-2 text-[10px] leading-snug text-slate-600">{it.sub}</p>
          </div>
        ))}
      </div>

      <div className="hidden grid-cols-1 gap-3 sm:gap-4 md:grid md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:ring-0 border-l-4 border-[#2f6fd4]">
          <div className="mb-4 flex items-center gap-3">
            <div className={`rounded-xl p-3 ${items[0].bg} ${items[0].icon}`}>
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-[#0f3350]">{t('financial.kpi.balanceCardTitle')}</h3>
          </div>
          <p className="mb-1 text-2xl font-bold tabular-nums tracking-tight text-[#0f3350]">
            {fmt(soldeActuel)} <span className="text-sm font-semibold text-slate-600">FBU</span>
          </p>
          <p className="text-sm text-slate-600">{t('financial.kpi.balanceCardDesc')}</p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm border-l-4 border-emerald-500">
          <div className="mb-3 flex items-center gap-3">
            <div className="rounded-xl bg-emerald-50 p-3 text-emerald-800">
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-[#0f3350]">{t('financial.kpi.inCardTitle')}</h3>
          </div>
          <p className="mb-1 text-2xl font-bold tabular-nums tracking-tight text-[#0f3350]">
            {fmt(totalEntrees)} <span className="text-sm font-semibold text-slate-600">FBU</span>
          </p>
          <p className="text-sm text-slate-600">{t('financial.kpi.inCardDesc')}</p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm border-l-4 border-red-500">
          <div className="mb-3 flex items-center gap-3">
            <div className="rounded-xl bg-red-50 p-3 text-red-800">
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-[#0f3350]">{t('financial.kpi.outCardTitle')}</h3>
          </div>
          <p className="mb-1 text-2xl font-bold tabular-nums tracking-tight text-[#0f3350]">
            {fmt(totalSorties)} <span className="text-sm font-semibold text-slate-600">FBU</span>
          </p>
          <p className="text-sm text-slate-600">{t('financial.kpi.outCardDesc')}</p>
        </div>
      </div>
    </>
  );
}
